import React, { memo, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { actionDetails, saveAction, removeAction } from '../../services/actions';
import Switch from '../../components/shared/switch';

import ContentTranslation from '../../components/ContentTranslation';
import { filterEmptyTranslations } from '../../services/filters';
import { exerciseAddons } from '../../services/exercises';
import Select from 'react-select';
import ImagePicker from '../../components/shared/ImagePicker';
import ConfirmRemoveDialog from '../../components/ConfirmRemoveDialog';
import ReturnButton from '../../components/ReturnButton';

const ActionForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { t } = useTranslation();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [language, setLanguage] = useState('en');
  const [actionTypes, setActionTypes] = useState([]);
  const [action, setAction] = useState({
    aktion_translations_attributes: [
      {
        title: '',
        content: '',
        locale: language,
        published: true,
      },
    ],
  });

  const loadData = async () => {
    if (!id) return;
    const { action } = await actionDetails(id);
    setAction(action);
  };

  useEffect(() => {
    const loadAddons = async () => {
      const { action_types } = await exerciseAddons();
      setActionTypes(action_types);
    };

    loadAddons();
  }, []);

  useEffect(() => {
    loadData();
  }, [location.pathname]);

  const updateAction = (field, value) => {
    setAction({ ...action, [field]: value });
  };

  const save = async () => {
    if (!!action?.locked) {
      return;
    }

    const data = { ...action };
    data.aktion_translations_attributes = filterEmptyTranslations(action.aktion_translations_attributes);

    const response = await saveAction(data);
    navigate('/club_admin/actions/');
  };

  const remove = async () => {
    if (!action?.id || !!action?.locked) {
      return;
    }
    setShowConfirmDialog(true);
  };

  const confirmRemove = async () => {
    await removeAction(action.id);
    navigate('/club_admin/actions/');
  };

  return (
    <div className="form-container action-form">
      <ReturnButton/>
      <div className="form-container-header">{t('actions.edit_action')}</div>
      <div className="form-container-header">
        <div className="form-container-header-title flex1">{action?.title}</div>
        <div className="form-container-header-status">
          {t('actions.published')}
          <Switch
            value={action?.published}
            onChange={value => updateAction('published', value)}
            disabled={!!action?.locked}
          />
        </div>
      </div>

      <div className="form-container-content">
        <ContentTranslation
          data={action?.aktion_translations_attributes}
          disabled={!!action?.locked}
          video={true}
          onChange={value => updateAction('aktion_translations_attributes', value)}
        />
      </div>

      <div className="heading">{t('actions.action_type')}</div>
      <Select
        closeMenuOnSelect={true}
        options={actionTypes.map(item => ({
          value: t(`exercises.editor.action_types.${item}`, item),
          label: t(`exercises.editor.action_types.${item}`, item),
        }))}
        onChange={selected => updateAction('action_type', selected.value)}
        value={{
          value: t(`exercises.editor.action_types.${action.action_type}`),
          label: t(`exercises.editor.action_types.${action.action_type}`, action.action_type),
        }}
      />

      <div className="heading">{t('actions.preview_image')}</div>
      <ImagePicker
        imageUrl={action.video_preview_image_app_url}
        onChange={file => updateAction('video_preview_image', file)}
        crop={true}
      />

      <div className="form-container-actions">
        <button className="button button-delete" disabled={!!action?.locked || !action?.id} onClick={remove}>
          {t('buttons.action.delete_action')}
        </button>
        <div className="flex-end">
          <button className="button" onClick={() => navigate('/club_admin/actions/')}>
            {t('buttons.common.cancel')}
          </button>
          <button className="button button-primary" disabled={!!action?.locked} onClick={save}>
            {t('buttons.common.save-changes')}
          </button>
        </div>
      </div>

      {showConfirmDialog && (
        <ConfirmRemoveDialog
          title={t(`actions.remove_action`, { title: action?.title })}
          confirm={confirmRemove}
          close={() => setShowConfirmDialog(false)}
        />
      )}
    </div>
  );
};

export default memo(ActionForm);
